import { QUICK_ANSWERS } from './quickAnswers';

/* ─────────────────────────────────────────────────────────────────────────
   "How to reach" corridor config: one entry per SEO landing page.
   Used by pages/HowToReach.jsx (detail) and pages/HowToReachIndex.jsx (hub).
   Adding a corridor here automatically creates its page + index card.
   ───────────────────────────────────────────────────────────────────────── */
const BASE_CORRIDORS = {
    'mumbai-to-munnar': {
        from: { name: 'Mumbai', lat: 18.9582347, lng: 72.8319514, type: 'google' },
        to: { name: 'Munnar', lat: 10.0889333, lng: 77.0595248, type: 'google' },
        title: 'Mumbai to Munnar: Train, Bus, Flight & Cab Options | RouteChef',
        metaDescription:
            'Plan Mumbai to Munnar by flight, train, bus or road. Compare practical gateways, transfer distances, journey times and date-specific route options.',
        quickAnswer:
            'The fastest way to reach Munnar from Mumbai is a ~2 hour flight from Mumbai (BOM) to Kochi (COK), followed by a 106.7 km cab ride (about 3–3.5 hours) up the ghats to Munnar. The cheapest way is a direct train from Lokmanya Tilak Terminus to Ernakulam Jn, then a 124 km cab.',
        intro: [
            "Munnar, Kerala's most loved hill station, has no airport and no railway station of its own, so every trip ends with a scenic road climb through tea estates. That makes choosing the right gateway city the most important decision of your journey from Mumbai.",
            'You have two practical gateways: fly into Kochi and drive up, or take a direct overnight train to Ernakulam (Kochi), Pollachi or Madurai and complete the trip by cab. Below is exactly how each option works, with real distances and typical journey times.',
        ],
        gateways: [
            { name: 'Kochi Airport (COK)', type: 'Airport', distanceKm: 106.7, cabTime: '3–3.5 hrs' },
            { name: 'Pollachi Jn (POY)', type: 'Railway Station', distanceKm: 115.3, cabTime: '3–3.5 hrs' },
            { name: 'Ernakulam Jn (ERS)', type: 'Railway Station', distanceKm: 124, cabTime: '3.5–4 hrs' },
            { name: 'Madurai Jn (MDU)', type: 'Railway Station', distanceKm: 158.6, cabTime: '4–4.5 hrs' },
        ],
        options: [
            {
                id: 'flight-kochi',
                icon: 'flight',
                badge: 'Fastest · Recommended',
                title: 'Fly Mumbai → Kochi, then cab to Munnar',
                duration: '≈ 5.5–6 hrs door to door',
                fareNote: 'Flights from ~₹5,700 · Cab ~₹3,500–5,000',
                modesParam: 'Flight',
                body:
                    'Multiple daily non-stop flights connect Mumbai (BOM) and Navi Mumbai (NMI) to Kochi (COK) in about 2 hours. IndiGo, Akasa Air and Air India Express all fly this route. From Kochi airport, Munnar is a 106.7 km drive (3–3.5 hours) through Neriamangalam and Adimali, one of the prettiest airport transfers in India. Land by early afternoon and you will reach Munnar before sunset.',
            },
            {
                id: 'train-ernakulam',
                icon: 'train',
                badge: 'Cheapest',
                title: 'Direct train Mumbai → Ernakulam Jn, then cab',
                duration: '≈ 26–29 hrs door to door',
                fareNote: 'Trains from ~₹1,900 (SL/3A) · Cab ~₹3,500–5,500',
                modesParam: 'Train',
                body:
                    'Direct daily trains run from Lokmanya Tilak Terminus (LTT) to Ernakulam Jn (ERS). The 16345 Netravati Express (24h 50m) and the faster 12223 LTT–ERS Duronto (22h 5m) are the popular picks. From Ernakulam, Munnar is a 124 km cab ride (3.5–4 hours). Board an evening departure, sleep through the Konkan coast, and drive up the next afternoon.',
            },
            {
                id: 'train-pollachi',
                icon: 'train',
                badge: 'Shortest cab from a station',
                title: 'Train to Pollachi Jn, then cab via Marayoor',
                duration: '≈ 27–30 hrs door to door',
                fareNote: 'Cab ~₹3,200–4,800',
                modesParam: 'Train',
                body:
                    'Pollachi Jn (Tamil Nadu side) is the closest railway station to Munnar by road distance at 115.3 km, approached via the quieter Udumalpet–Marayoor route past Chinnar Wildlife Sanctuary and the sandalwood forests. Connectivity from Mumbai usually involves one change, so it suits travellers who prefer the offbeat eastern approach.',
            },
            {
                id: 'train-madurai',
                icon: 'train',
                badge: 'Temple-circuit combo',
                title: 'Train to Madurai Jn, then cab via Theni',
                duration: '≈ 30+ hrs door to door',
                fareNote: 'Cab ~₹4,000–6,000',
                modesParam: 'Train',
                body:
                    'Madurai Jn is 158.6 km from Munnar. It is the longest cab leg of the four, but it lets you pair the Meenakshi Temple with your hill-station trip. The drive climbs through Theni and Bodinayakkanur into the Western Ghats. Pick this if Madurai itself is on your itinerary.',
            },
        ],
        steps: [
            'Book a morning Mumbai (BOM) → Kochi (COK) flight, about 2 hours in the air.',
            'Pre-book a cab from Kochi airport to Munnar (106.7 km). RouteChef shows hatchback, sedan and SUV fares upfront.',
            'Drive up via Neriamangalam and Adimali, around 3 to 3.5 hours with a tea break at Valara or Cheeyappara waterfalls.',
            'Reach Munnar by evening. Most hotels are around Old Munnar or along the Chinnakanal road.',
        ],
        faqs: [
            {
                q: 'Which is the nearest airport to Munnar?',
                a: 'Kochi International Airport (COK) is the nearest airport to Munnar, 106.7 km away, roughly a 3 to 3.5 hour drive. Madurai and Coimbatore airports are alternatives but involve longer road journeys.',
            },
            {
                q: 'Which is the nearest railway station to Munnar?',
                a: 'By road distance, Pollachi Jn (115.3 km) is the closest station, followed by Ernakulam Jn (124 km) and Madurai Jn (158.6 km). Ernakulam has the best direct connectivity from Mumbai.',
            },
            {
                q: 'Is there a direct train from Mumbai to Munnar?',
                a: 'No. Munnar has no railway station. The practical rail route is a direct train from Lokmanya Tilak Terminus to Ernakulam Jn, such as the 16345 Netravati Express or 12223 Duronto, followed by a 124 km cab to Munnar.',
            },
            {
                q: 'How much does a cab from Kochi airport to Munnar cost?',
                a: 'A one-way drop typically costs around ₹3,500–5,000 depending on the car type (hatchback, sedan or SUV). RouteChef shows live cab fares alongside your flight or train so you can budget the full door-to-door trip.',
            },
            {
                q: 'What is the cheapest way to reach Munnar from Mumbai?',
                a: 'A sleeper or 3A ticket on a direct LTT → Ernakulam train (from about ₹1,900) plus a shared or budget cab from Ernakulam is the cheapest practical combination, bringing the total under ₹4,000 per person for most dates.',
            },
        ],
    },
    'mumbai-to-varkala': {
        from: { name: 'Mumbai', lat: 18.9582347, lng: 72.8319514, type: 'google' },
        to: { name: 'Varkala', lat: 8.7378685, lng: 76.7163359, type: 'google' },
        title: 'Mumbai to Varkala: Train, Bus, Flight & Cab Options | RouteChef',
        metaDescription:
            'The complete Mumbai to Varkala travel guide: flights to Trivandrum, the direct Netravati Express, trains to Kollam Jn, cab distances, journey times and live fares.',
        quickAnswer:
            'The fastest way to reach Varkala from Mumbai is a ~2 hour flight to Trivandrum (TRV), then a 41.4 km cab (about 1–1.5 hours) to Varkala cliff. Uniquely, Varkala also has its own railway station, and the 16345 Netravati Express runs direct from Lokmanya Tilak Terminus to Varkala Sivagiri in about 29 hours.',
        intro: [
            "Varkala sits on Kerala's south coast, famous for its dramatic red cliff over the Arabian Sea. Unlike most beach and hill escapes, it actually has its own railway station, Varkala Sivagiri, barely 10 minutes from the cliff, so a genuinely direct train from Mumbai exists. The trade-off is time: the rail journey takes over a day, while flying to Trivandrum gets you on the cliff by lunch.",
            'Your three practical patterns: fly to Trivandrum and cab it (fastest), take the direct Netravati Express (zero transfers), or take a faster/cheaper train to Kollam Jn and cover the last 33.6 km by cab. There is also a clever mixed option via Kochi when Trivandrum fares spike.',
        ],
        gateways: [
            { name: 'Varkala Sivagiri (VAK)', type: 'Railway Station', distanceKm: 3, cabTime: '10 min' },
            { name: 'Kollam Jn (QLN)', type: 'Railway Station', distanceKm: 33.6, cabTime: '45–60 min' },
            { name: 'Trivandrum Airport (TRV)', type: 'Airport', distanceKm: 41.4, cabTime: '1–1.5 hrs' },
        ],
        options: [
            {
                id: 'flight-trivandrum',
                icon: 'flight',
                badge: 'Fastest · Recommended',
                title: 'Fly Mumbai → Trivandrum, then cab to Varkala',
                duration: '≈ 4–4.5 hrs door to door',
                fareNote: 'Flights from ~₹6,000 · Cab ~₹1,200–1,800',
                modesParam: 'Flight',
                body:
                    'Non-stop flights from Mumbai (BOM) and Navi Mumbai (NMI) reach Trivandrum (TRV) in just over 2 hours. From the airport, Varkala is a 41.4 km drive up the coast, taking 1 to 1.5 hours depending on Kazhakkoottam traffic. Take a morning flight and you are watching the sunset from the cliff the same evening.',
            },
            {
                id: 'train-direct-varkala',
                icon: 'train',
                badge: 'Direct · No cab needed',
                title: 'Direct train Mumbai → Varkala Sivagiri',
                duration: '≈ 29 hrs, zero transfers',
                fareNote: 'From ~₹700 (SL) · ~₹1,925 (3A)',
                modesParam: 'Train',
                body:
                    'The 16345 Netravati Express departs Lokmanya Tilak Terminus at 11:40 and pulls into Varkala Sivagiri at 16:51 the next day: a 29 hour coastal run down the Konkan with no changes and no cab at the end. The station is about 3 km from the cliff; autos do the last hop in 10 minutes. The slowest option on paper, but the simplest by far.',
            },
            {
                id: 'train-kollam',
                icon: 'train',
                badge: 'Cheapest',
                title: 'Train to Kollam Jn, then a 33.6 km cab',
                duration: '≈ 27–29 hrs door to door',
                fareNote: 'Trains from ~₹1,305 (3A Garib Rath) · Cab ~₹1,000–1,500',
                modesParam: 'Train',
                body:
                    'Kollam Jn is a bigger junction with more Mumbai trains and often better availability: the 12201 KCVL Garib Rath (LTT 16:45 → Kollam 18:12 next day, about 25.5 hours) is both the cheapest and quickest rail option, and the 20910 Kochuveli Express and 16335 Nagercoil Express run from Vasai Road. From Kollam, Varkala is a 33.6 km cab ride of 45–60 minutes along the coast.',
            },
            {
                id: 'flight-kochi-train',
                icon: 'flight',
                badge: 'Smart mixed option',
                title: 'Fly Mumbai → Kochi, then train down to Kollam',
                duration: '≈ 9–10 hrs door to door',
                fareNote: 'Flights from ~₹5,500 · Train ~₹150 (SL)',
                modesParam: '',
                body:
                    'When Trivandrum fares spike, Kochi (COK) flights are often noticeably cheaper. Land at Kochi, transfer 27 km to Ernakulam Town, and ride a passenger train like the 16792 Palaruvi Express down to Kollam (about 4.5 hours, tickets from ₹150), finishing with the short cab to Varkala. More moving parts, but frequently the best fare-to-time balance, and RouteChef stitches this combination automatically.',
            },
        ],
        steps: [
            'Book a morning Mumbai (BOM/NMI) → Trivandrum (TRV) flight, about 2 hours in the air.',
            'Pre-book a cab from Trivandrum airport to Varkala (41.4 km). RouteChef shows hatchback, sedan and SUV fares upfront.',
            'Drive up the coastal highway past Kazhakkoottam and Attingal, 1 to 1.5 hours.',
            'Reach Varkala cliff by early afternoon. Most stays cluster along North Cliff and Odayam beach.',
        ],
        faqs: [
            {
                q: 'Which is the nearest airport to Varkala?',
                a: 'Trivandrum International Airport (TRV) is the nearest airport, 41.4 km from Varkala, roughly a 1 to 1.5 hour drive. Kochi airport is an alternative when fares are cheaper, combined with a train down to Kollam or Varkala.',
            },
            {
                q: 'Which is the nearest railway station to Varkala?',
                a: 'Varkala has its own station, Varkala Sivagiri (VAK), about 3 km from the cliff. Kollam Jn (33.6 km) is the nearest major junction, with more trains from Mumbai and better seat availability.',
            },
            {
                q: 'Is there a direct train from Mumbai to Varkala?',
                a: 'Yes. The 16345 Netravati Express runs daily from Lokmanya Tilak Terminus to Varkala Sivagiri, departing 11:40 and arriving 16:51 the next day (about 29 hours), with no transfers needed.',
            },
            {
                q: 'How much does a cab from Trivandrum airport to Varkala cost?',
                a: 'A one-way drop for the 41.4 km ride typically costs around ₹1,200–1,800 depending on the car type. RouteChef shows live cab fares alongside your flight so you can budget the full door-to-door trip.',
            },
            {
                q: 'What is the cheapest way to reach Varkala from Mumbai?',
                a: 'A 3A berth on the 12201 KCVL Garib Rath to Kollam Jn (from about ₹1,305) plus a short cab, or a sleeper berth on the direct Netravati Express (from about ₹700) with no cab at all. Both keep the total under ₹2,000 for most dates.',
            },
        ],
    },
    'pune-to-varkala': {
        from: { name: 'Pune', lat: 18.5246091, lng: 73.8786239, type: 'google' },
        to: { name: 'Varkala', lat: 8.7378685, lng: 76.7163359, type: 'google' },
        title: 'How to Reach Varkala from Pune (2026): Flight + Train Combo, Direct Train & Cab',
        metaDescription:
            'The complete Pune to Varkala travel guide: the fast flight-to-Kochi + train combo, the direct Pune–Cape Express, trains to Ernakulam & Kollam, cab distances and live fares.',
        quickAnswer:
            'The fastest way to reach Varkala from Pune is an early flight to Kochi (~2 hours), a connecting train down to Kollam and a short cab, about 8.5 hours door to door. Pune also has a direct train: the 16381 Pune–Cape Express runs all the way to Varkala Sivagiri station in about 32 hours, with no cab needed at the end.',
        intro: [
            'Varkala\u2019s cliff-top beaches are a long way from Pune (around 1,300 km), but the journey is friendlier than the distance suggests. Pune has something even Mumbai lacks for this corridor: a direct train that stops at Varkala Sivagiri itself, plus a clever flight-and-train combination through Kochi that gets you from Pune to the cliff in a single morning.',
            'There is no non-stop Pune–Trivandrum flight, which changes the usual playbook: flying into Kochi and riding the rails down the coast beats connecting flights on both time and fare for most dates. Here is how every option works.',
        ],
        gateways: [
            { name: 'Varkala Sivagiri (VAK)', type: 'Railway Station', distanceKm: 3, cabTime: '10 min' },
            { name: 'Kollam Jn (QLN)', type: 'Railway Station', distanceKm: 33.6, cabTime: '45–60 min' },
            { name: 'Trivandrum Airport (TRV)', type: 'Airport', distanceKm: 41.4, cabTime: '1–1.5 hrs' },
        ],
        options: [
            {
                id: 'flight-kochi-train',
                icon: 'flight',
                badge: 'Fastest · Recommended',
                title: 'Fly Pune → Kochi, then train down to Kollam',
                duration: '≈ 8.5–9 hrs door to door',
                fareNote: 'Total from ~₹7,000 (flight + train + cab)',
                modesParam: '',
                body:
                    'Early flights connect Pune (PNQ) to Kochi (COK) in under 2 hours. The 01:35 Air India Express 2739 lands at 03:30, and IndiGo\u2019s 03:05 departure follows it. A 27 km cab brings you to Ernakulam Town station for the 07:15 Kanyakumari Express, which reaches Kollam at 10:10 (3A from ₹520). One short 33.6 km cab later, you are on Varkala cliff by lunchtime. RouteChef stitches this exact flight + train + cab combination in one search.',
            },
            {
                id: 'train-direct-varkala',
                icon: 'train',
                badge: 'Direct · No cab needed',
                title: 'Direct train: 16381 Pune–Cape Express to Varkala Sivagiri',
                duration: '≈ 32 hrs, zero transfers',
                fareNote: 'From ~₹700 (SL) · ~₹1,960 (3A)',
                modesParam: 'Train',
                body:
                    'The 16381 Pune–Kanyakumari (Cape) Express leaves Pune Jn at 23:45 and rolls into Varkala Sivagiri at 07:40 on the second morning. It takes about 32 hours, but with a berth the whole way, no transfers, and the station just 3 km from the cliff. For travellers who prefer one booking and zero logistics, this is the simplest route to Varkala from anywhere in Maharashtra.',
            },
            {
                id: 'train-ernakulam-combo',
                icon: 'train',
                badge: 'Budget rail combo',
                title: 'Overnight train to Ernakulam, connect to Kollam',
                duration: '≈ 33–34 hrs door to door',
                fareNote: 'Trains ~₹1,680 + ₹520 · Cab ~₹1,000–1,500',
                modesParam: 'Train',
                body:
                    'The 11097 Poorna Express departs Pune Jn at 22:50 and reaches Ernakulam Town at 02:20 two nights later. A quick 3.6 km hop to Ernakulam Jn connects you to the 04:15 Kochuveli Express, arriving Kollam at 07:22, with the usual 33.6 km cab to finish. More changes than the direct train, but it opens up additional departure days and often better berth availability.',
            },
            {
                id: 'flight-trivandrum',
                icon: 'flight',
                badge: 'When timing beats fare',
                title: 'Fly Pune → Trivandrum (via Bengaluru), then cab',
                duration: '≈ 7–8 hrs door to door',
                fareNote: 'Connecting flights from ~₹12,000 · Cab ~₹1,200–1,800',
                modesParam: 'Flight',
                body:
                    'There is no non-stop Pune–Trivandrum service, so this route means a connection, typically IndiGo via Bengaluru, about 5 hours 10 minutes in total, landing at TRV by midday. From the airport, Varkala is a 41.4 km cab up the coast. It costs notably more than the Kochi combination, but suits travellers who want a single-mode air journey with daytime timings.',
            },
        ],
        steps: [
            'Book the early Pune (PNQ) → Kochi (COK) flight (the 01:35 or 03:05 departure), about 2 hours in the air.',
            'Take a pre-booked cab from Kochi airport to Ernakulam Town station (27.2 km, ~48 minutes).',
            'Board the 07:15 Kanyakumari Express to Kollam Jn, 3 hours along the backwater country (3A from ₹520).',
            'Finish with the 33.6 km cab from Kollam to Varkala. You are on the cliff by lunchtime.',
        ],
        faqs: [
            {
                q: 'Is there a direct train from Pune to Varkala?',
                a: 'Yes. The 16381 Pune–Kanyakumari (Cape) Express runs direct from Pune Jn to Varkala Sivagiri, departing 23:45 and arriving 07:40 on the second morning, roughly 32 hours with no transfers.',
            },
            {
                q: 'Is there a direct flight from Pune to Trivandrum?',
                a: 'No. Pune–Trivandrum requires a connection, usually via Bengaluru (about 5 hours in total). That is why flying into Kochi and taking a train down to Kollam is usually faster and cheaper from Pune.',
            },
            {
                q: 'What is the fastest way from Pune to Varkala?',
                a: 'The early flight to Kochi, the 07:15 Kanyakumari Express to Kollam, and a 33.6 km cab: around 8.5 hours door to door and typically about ₹7,000 in total.',
            },
            {
                q: 'Which is the nearest airport and railway station to Varkala?',
                a: 'Trivandrum airport (TRV) is the nearest airport at 41.4 km. Varkala has its own station, Varkala Sivagiri, 3 km from the cliff, while Kollam Jn (33.6 km) is the nearest major junction with the widest train connectivity.',
            },
            {
                q: 'What is the cheapest way to reach Varkala from Pune?',
                a: 'A sleeper berth on the direct 16381 Pune–Cape Express (from about ₹700, no cab needed) is the cheapest practical option; 3A costs about ₹1,960. Rail combos via Ernakulam land in a similar range.',
            },
        ],
    },
    'mumbai-to-rishikesh': {
        from: { name: 'Mumbai', lat: 18.9582347, lng: 72.8319514, type: 'google' },
        to: { name: 'Rishikesh', lat: 30.0869281, lng: 78.2676116, type: 'google' },
        title: 'How to Reach Rishikesh from Mumbai (2026): Direct Flight, Trains & Cab Options',
        metaDescription:
            'The complete Mumbai to Rishikesh travel guide: direct flights to Dehradun, the Bandra–Haridwar Express, Vande Bharat & Janshatabdi combos via Delhi, cab distances and live fares.',
        quickAnswer:
            'The fastest way to reach Rishikesh from Mumbai is a direct ~2.5 hour flight to Dehradun (Jolly Grant airport), followed by a short 15.5 km cab, under 4 hours door to door. The cheapest is the direct 19019 Bandra Terminus–Haridwar Express (about 31.5 hours), with a 25.1 km cab from Haridwar Jn to finish.',
        intro: [
            'Rishikesh, the yoga capital on the Ganga, has no airport and only limited train service of its own, so every journey from Mumbai routes through one of two gateways: Dehradun\u2019s Jolly Grant airport, just 15.5 km away, or Haridwar Jn, 25.1 km down the river.',
            'The good news is that both gateways are unusually well connected from Mumbai: direct flights reach Jolly Grant in about two and a half hours, a direct overnight-plus train runs from Bandra Terminus to Haridwar, and if you fly into Delhi instead, the Vande Bharat and Dehradun Janshatabdi both make the Haridwar hop in under 4 hours the same afternoon.',
        ],
        gateways: [
            { name: 'Jolly Grant Airport, Dehradun (DED)', type: 'Airport', distanceKm: 15.5, cabTime: '30–40 min' },
            { name: 'Haridwar Jn (HW)', type: 'Railway Station', distanceKm: 25.1, cabTime: '35–45 min' },
            { name: 'Yog Nagari Rishikesh (YNRK)', type: 'Railway Station', distanceKm: 2, cabTime: '5–10 min' },
        ],
        options: [
            {
                id: 'flight-dehradun',
                icon: 'flight',
                badge: 'Fastest · Recommended',
                title: 'Fly Mumbai → Dehradun direct, then a short cab',
                duration: '≈ 3.5–4 hrs door to door',
                fareNote: 'Flights from ~₹6,700 · Cab ~₹700–1,200',
                modesParam: 'Flight',
                body:
                    'IndiGo flies non-stop from Navi Mumbai (NMI) to Dehradun\u2019s Jolly Grant airport in about 2 hours 20 minutes; the 15:15 departure lands at 17:35. From there Rishikesh is only 15.5 km, one of the shortest airport transfers to any Indian getaway: 30–40 minutes and you are at Tapovan or Ram Jhula. Leave Mumbai after lunch, catch the evening Ganga aarti.',
            },
            {
                id: 'flight-delhi-train',
                icon: 'flight',
                badge: 'Most departures · Same-day',
                title: 'Fly to Delhi, then Vande Bharat or Janshatabdi to Haridwar',
                duration: '≈ 9–10 hrs door to door',
                fareNote: 'Flights from ~₹5,000 · Train CC ₹495–985 · Cab ~₹900–1,400',
                modesParam: '',
                body:
                    'Mumbai–Delhi has flights nearly every hour, which makes this the most flexible routing. Land by early afternoon, transfer to the station, and take either the 22457 Vande Bharat Express from Anand Vihar (17:50 → Haridwar 21:11, CC ₹985) or the 12055 Dehradun Janshatabdi from New Delhi (15:20 → Haridwar 19:27, CC ₹495). A 25.1 km cab completes the trip the same evening. RouteChef stitches the flight, the station transfer and the train in one search.',
            },
            {
                id: 'train-direct-haridwar',
                icon: 'train',
                badge: 'Direct · Cheapest',
                title: 'Direct train: 19019 Bandra Terminus–Haridwar Express',
                duration: '≈ 32 hrs door to door',
                fareNote: 'From ~₹700 (SL) · ~₹1,810 (3A) · Cab ~₹900–1,400',
                modesParam: 'Train',
                body:
                    'The 19019 BDTS–Haridwar Express leaves Bandra Terminus at 00:20 and arrives Haridwar Jn at 07:50 the next morning, about 31.5 hours with a berth the whole way. From Haridwar it is a 38-minute, 25.1 km drive along the Ganga to Rishikesh. Slow, but it is the only single-ticket option from Mumbai and comfortably the cheapest.',
            },
        ],
        steps: [
            'Book the afternoon Navi Mumbai (NMI) → Dehradun (DED) non-stop, about 2 hours 20 minutes in the air.',
            'Pre-book a cab from Jolly Grant airport to Rishikesh (15.5 km). RouteChef shows hatchback, sedan and SUV fares upfront.',
            'Drive 30–40 minutes past Raiwala into Rishikesh. Tapovan and Laxman Jhula are slightly beyond the town centre.',
            'Arrive before sunset, in time for the evening Ganga aarti at Triveni Ghat or Parmarth Niketan.',
        ],
        faqs: [
            {
                q: 'Which is the nearest airport to Rishikesh?',
                a: 'Jolly Grant Airport, Dehradun (DED) is the nearest airport, just 15.5 km from Rishikesh, a 30–40 minute drive. It has direct flights from Mumbai, Delhi and other metros.',
            },
            {
                q: 'Which is the nearest railway station to Rishikesh?',
                a: 'Rishikesh has its own station, Yog Nagari Rishikesh (YNRK), about 2 km from town, but long-distance service there is limited. Haridwar Jn, 25.1 km away, is the practical rail gateway with far more trains, including direct service from Mumbai.',
            },
            {
                q: 'Is there a direct train from Mumbai to Rishikesh?',
                a: 'Not to Rishikesh itself, but the 19019 Bandra Terminus–Haridwar Express runs direct to Haridwar Jn (00:20 → 07:50 next day, about 31.5 hours), leaving only a 25.1 km cab ride to Rishikesh.',
            },
            {
                q: 'What is the fastest way from Mumbai to Rishikesh?',
                a: 'The direct Navi Mumbai → Dehradun flight (about 2.5 hours) plus the 15.5 km cab: under 4 hours door to door. Flying to Delhi and catching the Vande Bharat or Janshatabdi to Haridwar is the next-best, arriving the same evening.',
            },
            {
                q: 'How much does a cab from Dehradun airport or Haridwar to Rishikesh cost?',
                a: 'From Jolly Grant airport (15.5 km) expect roughly ₹700–1,200 one way; from Haridwar Jn (25.1 km) roughly ₹900–1,400 depending on the car type. RouteChef shows live cab fares alongside your flight or train.',
            },
        ],
    },
    'bangalore-to-coorg': {
        from: { name: 'Bangalore', lat: 12.9767936, lng: 77.590082, type: 'google' },
        to: { name: 'Coorg', lat: 12.4207575, lng: 75.7397482, type: 'google' },
        title: 'Bangalore to Coorg Trains: Routes, Stations & Travel Options',
        metaDescription:
            'Looking for Bangalore to Coorg trains? Compare rail routes to Mysuru and Subrahmanya Road, onward cab transfers to Madikeri, direct buses, flights to nearby airports and live travel options.',
        quickAnswer:
            'Coorg (Madikeri) has no airport and no railway station, so every route ends with a scenic drive. The quickest combination from Bangalore is a 1.5 hour flight to Kannur followed by an 89.4 km cab; by rail, take a train to Subrahmanya Road (85.7 km cab) or Mysuru Jn (117.2 km cab via Kushalnagar). Many travellers simply drive the ~260 km directly in 5.5–6 hours.',
        intro: [
            'Coorg is Karnataka\u2019s coffee country: misty ridges around Madikeri with no airport and no rail line of its own. From Bangalore that leaves a genuine choice: drive the whole way, or shorten the road leg by taking a train or flight to one of three gateways that approach the hills from completely different directions.',
            'Subrahmanya Road station brings you in from the south past Kukke Subrahmanya, Mysuru opens the classic Kushalnagar route past Bylakuppe\u2019s golden monastery, and Kannur airport climbs in from the Kerala side. Here is how each plays out with real times and fares.',
        ],
        gateways: [
            { name: 'Subrahmanya Road (SBHR)', type: 'Railway Station', distanceKm: 85.7, cabTime: '2–2.5 hrs' },
            { name: 'Kannur Airport (CNN)', type: 'Airport', distanceKm: 89.4, cabTime: '2–2.5 hrs' },
            { name: 'Mysuru Jn (MYS)', type: 'Railway Station', distanceKm: 117.2, cabTime: '~3 hrs' },
        ],
        options: [
            {
                id: 'train-subrahmanya',
                icon: 'train',
                badge: 'Best rail combo',
                title: 'Train to Mysuru, then cab to Madikeri via Kushalnagar',
                duration: '≈ 10 hrs door to door',
                fareNote: 'Train from ~₹555 (3E) · Cab ~₹2,500–3,500',
                modesParam: 'Train',
                body:
                    'A practical rail option is to take a train from KSR Bengaluru to Mysuru Junction, then continue by cab towards Madikeri via Kushalnagar. The rail leg is short compared with the onward road journey, so compare departure time, arrival time, transfer cost and your exact stay location before choosing this combination.',
            },
            {
                id: 'flight-kannur',
                icon: 'flight',
                badge: 'Quickest',
                title: 'Fly Bangalore → Kannur, then cab from the Kerala side',
                duration: '≈ 5–5.5 hrs door to door',
                fareNote: 'Flights from ~₹6,200 · Cab ~₹2,800–4,000',
                modesParam: 'Flight',
                body:
                    'IndiGo\u2019s evening hop from Bangalore to Kannur takes just 1 hour 25 minutes, and Coorg is an 89.4 km drive from the airport, climbing through Iritty and Virajpet on the quieter western approach. Door to door it edges out the direct drive on time while skipping Bangalore\u2019s exit traffic entirely. It is worth it when Mysuru-road congestion is at its weekend worst.',
            },
            {
                id: 'train-mysuru',
                icon: 'train',
                badge: 'Most frequent · Classic route',
                title: 'Train to Mysuru, then the Kushalnagar road',
                duration: '≈ 7–8 hrs door to door',
                fareNote: 'Trains from ~₹270 (CC) · Cab ~₹3,000–4,500',
                modesParam: 'Train',
                body:
                    'Bangalore–Mysuru has trains nearly every hour. The 16551 MAS–AP Express does it in 3 hours 25 minutes with chair car seats from ₹270, and Shatabdi or Vande Bharat services are faster still. From Mysuru, the 117.2 km drive to Coorg runs via Kushalnagar, passing Bylakuppe\u2019s Namdroling Monastery and the Dubare elephant camp, and many travellers treat the cab leg as the first sightseeing day. Pick a morning departure so the ~3 hour drive finishes in daylight.',
            },
        ],
        steps: [
            'Book the Vande Bharat Express from KSR Bengaluru (SBC) to Mysuru Junction: train 20664 departs at 21:30, while train 20607 departs at 10:05. Travel time is approximately 2 hours to 2 hours 15 minutes.',
            'Pre-book a cab from Mysuru Junction for the onward journey to Madikeri/Coorg via Kushalnagar. RouteChef shows available transfer options and fares where supported.',
            'Continue by cab from Mysuru to Coorg, allowing roughly 3 hours by road depending on traffic and the exact destination in Coorg.',
            'Reach Madikeri by early evening; most stays and estates are around Madikeri town, Kakkabe and Suntikoppa.',
        ],
        faqs: [
            {
                q: 'Which is the nearest airport to Coorg?',
                a: 'Kannur International Airport (CNN) is the closest by road at 89.4 km, about a 2 to 2.5 hour drive on the Kerala-side approach. Mysuru and Mangaluru airports are alternatives, and Bangalore itself works for those combining the flight with the classic road trip.',
            },
            {
                q: 'Which is the nearest railway station to Coorg?',
                a: 'Subrahmanya Road (SBHR) at 85.7 km is the closest, with a roughly 2 hour cab to Madikeri. Mysuru Jn (117.2 km) is farther but has far more frequent trains from Bangalore and the popular Kushalnagar route.',
            },
            {
                q: 'Is there a direct train or flight to Coorg?',
                a: 'No. Coorg has neither an airport nor a railway station. Every route combines a train or flight to a gateway (Subrahmanya Road, Mysuru or Kannur) with a 2–3 hour cab into the hills.',
            },
            {
                q: 'Is it better to drive directly from Bangalore to Coorg?',
                a: 'The direct drive is about 260 km and takes 5.5–6 hours via Mysuru and Kushalnagar. It is the most flexible option, but the train-plus-cab combinations avoid Bangalore\u2019s exit traffic and cost far less, and the Kannur flight beats the drive on time.',
            },
            {
                q: 'How much does the cab to Coorg cost from each gateway?',
                a: 'Expect roughly ₹2,500–3,500 from Subrahmanya Road (85.7 km), ₹2,800–4,000 from Kannur airport (89.4 km) and ₹3,000–4,500 from Mysuru (117.2 km), depending on car type. RouteChef shows live cab fares alongside your train or flight.',
            },
        ],
    },
    'bangalore-to-varkala': {
        from: { name: 'Bangalore', lat: 12.9767936, lng: 77.590082, type: 'google' },
        to: { name: 'Varkala', lat: 8.7378685, lng: 76.7163359, type: 'google' },
        title: 'Bangalore to Varkala: Train, Bus, Flight & Mixed-Mode Options | RouteChef',
        metaDescription:
            'The complete Bangalore to Varkala travel guide: direct overnight trains, flights to Trivandrum & Kochi, the Vande Bharat + Ernad Express same-day rail combo, and live fares.',
        quickAnswer:
            'The simplest fast way to reach Varkala from Bangalore is a morning flight to Trivandrum (1 hour 20 minutes) and a 41.4 km cab, putting you on the cliff by lunch. The clever alternative: fly to Kochi and board the Ernad Express, which runs directly to Varkala Sivagiri station by evening. Overnight, the direct 16526 Kanyakumari Express does Bangalore Cantt → Varkala in about 14 hours with no cab needed.',
        intro: [
            'Bangalore to Varkala can be planned by direct overnight train, flight with an onward road transfer, or connecting rail and mixed-mode options. The practical choice depends on your date, budget, total journey time, transfer requirements and whether a direct service has suitable availability.',
            'A quirk worth knowing: from Kochi, the Ernad Express stops at Varkala Sivagiri directly, so the flight-plus-train combination ends 10 minutes from the cliff with no long cab leg at all. Here is every option with real times and fares.',
        ],
        gateways: [
            { name: 'Varkala Sivagiri (VAK)', type: 'Railway Station', distanceKm: 3, cabTime: '10 min' },
            { name: 'Kollam Jn (QLN)', type: 'Railway Station', distanceKm: 33.6, cabTime: '45–60 min' },
            { name: 'Trivandrum Airport (TRV)', type: 'Airport', distanceKm: 41.4, cabTime: '1–1.5 hrs' },
        ],
        options: [
            {
                id: 'flight-trivandrum',
                icon: 'flight',
                badge: 'Quickest · By lunch',
                title: 'Fly Bangalore → Trivandrum, then cab to the cliff',
                duration: '≈ 4 hrs door to door',
                fareNote: 'Flights from ~₹4,900 · Cab ~₹1,200–1,800',
                modesParam: 'Flight',
                body:
                    'IndiGo\u2019s 07:20 departure reaches Trivandrum at 08:40, just 1 hour 20 minutes in the air, with more services through the morning. From TRV, Varkala is a 41.4 km coastal drive of about an hour and a quarter. Leave Bangalore at breakfast, swim before lunch.',
            },
            {
                id: 'flight-kochi-ernad',
                icon: 'flight',
                badge: 'Smart combo · Train ends at Varkala',
                title: 'Fly to Kochi, then the Ernad Express straight to Varkala',
                duration: '≈ 7 hrs door to door',
                fareNote: 'Flights from ~₹4,300 · Train CC ₹310 · No destination cab',
                modesParam: '',
                body:
                    'Kochi flights are often the cheapest out of Bangalore: AllianceAir\u2019s 07:05 and IndiGo\u2019s midday 12:55 both land in under 90 minutes. A 29.6 km cab brings you to Ernakulam Jn for the 16:20 Ernad Express, which runs down the coast and stops at Varkala Sivagiri itself at 19:57. Chair car costs ₹310, and the station is 10 minutes from the cliff. Faster morning connections via the 11:43 Vande Bharat to Kollam also exist; RouteChef stitches whichever lands best for your date.',
            },
            {
                id: 'train-direct-overnight',
                icon: 'train',
                badge: 'Direct · Overnight · No cab',
                title: 'Direct overnight train to Varkala Sivagiri',
                duration: '≈ 14–14.5 hrs, zero transfers',
                fareNote: 'From ~₹1,180 (3A) · departs 20:22, arrive 10:37',
                modesParam: 'Train',
                body:
                    'The 16526 Kanyakumari Express leaves Bangalore Cantt at 20:22 and pulls into Varkala Sivagiri at 10:37 the next morning, so you sleep through the whole journey and wake up 3 km from the beach. The 16315 Kochuveli Express (to Kollam, 16:47 → 07:22) and the KCVL Humsafar from Krishnarajapuram are solid alternates when Kanyakumari Express berths sell out; both leave a short 33.6 km cab from Kollam.',
            },
            {
                id: 'rail-sameday-vandebharat',
                icon: 'train',
                badge: 'Same-day by rail',
                title: 'Morning Vande Bharat to Ernakulam, connect to Varkala',
                duration: '≈ 15 hrs, all rail, same day',
                fareNote: 'CC ₹1,670 + ₹310',
                modesParam: 'Train',
                body:
                    'For an all-rail daylight run: the 26651 Ernakulam Vande Bharat departs KSR Bangalore at 05:10 and arrives 13:50, connecting to the same 16:20 Ernad Express that terminates the day at Varkala Sivagiri by 19:57. RouteChef\u2019s connection buffer here is computed from actual past runs of both trains (a comfortable two-hour-plus margin), so you can judge the transfer risk before booking.',
            },
        ],
        steps: [
            'Book the 07:20 Bangalore (BLR) → Trivandrum (TRV) flight, 1 hour 20 minutes in the air.',
            'Pre-book a cab from Trivandrum airport to Varkala (41.4 km). RouteChef shows hatchback, sedan and SUV fares upfront.',
            'Drive up the coast past Kazhakkoottam and Attingal, about an hour and a quarter.',
            'Reach Varkala cliff before lunch; stays cluster along North Cliff and Odayam beach.',
        ],
        faqs: [
            {
                q: 'Is there a direct train from Bangalore to Varkala?',
                a: 'Yes. The 16526 Kanyakumari Express runs direct from Bangalore Cantt to Varkala Sivagiri (20:22 → 10:37 next morning, about 14 hours), and a weekly SMVB–Trivandrum special also stops at Varkala. Several more trains serve nearby Kollam Jn.',
            },
            {
                q: 'Which is the nearest airport to Varkala from Bangalore?',
                a: 'Trivandrum (TRV), 41.4 km from Varkala, has the shortest onward cab and sub-90-minute flights from Bangalore. Kochi (COK) is the alternative. It is farther by road, but the Ernad Express from Ernakulam runs directly to Varkala station, eliminating the long cab.',
            },
            {
                q: 'What is the fastest way from Bangalore to Varkala?',
                a: 'The morning flight to Trivandrum plus the 41.4 km cab: about 4 hours door to door, reaching the cliff by lunchtime.',
            },
            {
                q: 'Can I reach Varkala from Bangalore the same day by train?',
                a: 'Yes. Take the 05:10 Ernakulam Vande Bharat (arrives 13:50) and connect to the 16:20 Ernad Express, which reaches Varkala Sivagiri at 19:57 the same evening.',
            },
            {
                q: 'What is the cheapest way to reach Varkala from Bangalore?',
                a: 'A sleeper or 3A berth on the direct overnight Kanyakumari Express (3A from about ₹1,180), with no cab needed at either end beyond the 3 km hop from Varkala Sivagiri station to the cliff.',
            },
        ],
    },
};

/* ─────────────────────────────────────────────────────────────────────────
   Destination gateways for generated corridors.
   Keyed by destination slug; every corridor ending at that destination
   inherits the same list. Distances are approximate road km from the
   destination's reference point (city centre / seed lat-lng).
   ORDER MATTERS: the hub card shows the first Airport and the first
   non-Airport entry, so list the primary gateway of each type first.
   ───────────────────────────────────────────────────────────────────────── */
const A = (name, distanceKm, cabTime) => ({ name, type: 'Airport', distanceKm, cabTime });
const S = (name, distanceKm, cabTime) => ({ name, type: 'Railway Station', distanceKm, cabTime });

const DESTINATION_GATEWAYS = {
    // ── North / Hills ──
    delhi: [
        A('Indira Gandhi International Airport (DEL)', 15, '40–60 min'),
        S('New Delhi (NDLS)', 2, '10–15 min'),
        S('Old Delhi (DLI)', 6, '20–30 min'),
        S('Hazrat Nizamuddin (NZM)', 8, '20–30 min'),
        S('Anand Vihar Terminal (ANVT)', 13, '35–50 min'),
    ],
    jaipur: [
        A('Jaipur International Airport (JAI)', 12, '30–40 min'),
        S('Jaipur Jn (JP)', 2, '10 min'),
        S('Gandhinagar Jaipur (GADJ)', 6, '15–20 min'),
    ],
    udaipur: [
        A('Maharana Pratap Airport (UDR)', 22, '35–45 min'),
        S('Udaipur City (UDZ)', 3, '10–15 min'),
        S('Rana Pratap Nagar (RPZ)', 5, '15 min'),
    ],
    amritsar: [
        A('Sri Guru Ram Dass Jee International Airport (ATQ)', 11, '25–35 min'),
        S('Amritsar Jn (ASR)', 2, '10 min'),
    ],
    dehradun: [
        A('Jolly Grant Airport (DED)', 25, '45–60 min'),
        S('Dehradun (DDN)', 2, '10 min'),
        S('Haridwar Jn (HW)', 52, '1.5–2 hrs'),
    ],
    rishikesh: [
        A('Jolly Grant Airport, Dehradun (DED)', 15.5, '30–40 min'),
        S('Haridwar Jn (HW)', 25.1, '35–45 min'),
        S('Yog Nagari Rishikesh (YNRK)', 2, '5–10 min'),
    ],
    manali: [
        A('Kullu–Manali Airport, Bhuntar (KUU)', 50, '1.5–2 hrs'),
        A('Chandigarh Airport (IXC)', 310, '7–8 hrs'),
        S('Chandigarh Jn (CDG)', 310, '7–8 hrs'),
    ],

    // ── UP / Bihar / Jharkhand ──
    varanasi: [
        A('Lal Bahadur Shastri Airport (VNS)', 25, '45–60 min'),
        S('Varanasi Jn (BSB)', 4, '15 min'),
        S('Banaras (BSBS)', 5, '15–20 min'),
        S('Pt. Deen Dayal Upadhyaya Jn (DDU)', 17, '40–50 min'),
    ],
    ayodhya: [
        A('Maharishi Valmiki International Airport (AYJ)', 10, '20–25 min'),
        S('Ayodhya Dham Jn (AY)', 2, '5–10 min'),
        S('Ayodhya Cantt (AYC)', 8, '15–20 min'),
        S('Gonda Jn (GD)', 52.4, 'About 1 hr 20 min; traffic varies'),
    ],
    prayagraj: [
        A('Prayagraj Airport (IXD)', 12, '30–40 min'),
        S('Prayagraj Jn (PRYJ)', 2, '10 min'),
        S('Prayagraj Chheoki (PCOI)', 12, '30–40 min'),
    ],
    lucknow: [
        A('Chaudhary Charan Singh International Airport (LKO)', 14, '30–45 min'),
        S('Lucknow Charbagh (LKO)', 2, '10 min'),
        S('Lucknow NER (LJN)', 2, '10 min'),
        S('Gomti Nagar (GTNR)', 10, '25–35 min'),
    ],
    kanpur: [
        A('Kanpur Airport (KNU)', 10, '25–30 min'),
        A('Lucknow Airport (LKO)', 75, '1.5–2 hrs'),
        S('Kanpur Central (CNB)', 2, '10 min'),
    ],
    gorakhpur: [
        A('Gorakhpur Airport (GOP)', 8, '20–25 min'),
        S('Gorakhpur Jn (GKP)', 2, '10 min'),
    ],
    patna: [
        A('Jay Prakash Narayan Airport (PAT)', 6, '20 min'),
        S('Patna Jn (PNBE)', 2, '10 min'),
        S('Rajendra Nagar Terminal (RJPB)', 4, '15 min'),
        S('Pataliputra Jn (PPTA)', 8, '20–25 min'),
        S('Danapur (DNR)', 12, '30–40 min'),
    ],
    darbhanga: [
        A('Darbhanga Airport (DBR)', 8, '20 min'),
        A('Patna Airport (PAT)', 135, '3–3.5 hrs'),
        S('Darbhanga Jn (DBG)', 2, '10 min'),
    ],
    muzaffarpur: [
        A('Patna Airport (PAT)', 75, '2–2.5 hrs'),
        A('Darbhanga Airport (DBR)', 70, '~2 hrs'),
        S('Muzaffarpur Jn (MFP)', 2, '10 min'),
    ],
    gaya: [
        A('Gaya Airport (GAY)', 12, '25–30 min'),
        S('Gaya Jn (GAYA)', 2, '10 min'),
    ],
    ranchi: [
        A('Birsa Munda Airport (IXR)', 7, '20 min'),
        S('Ranchi Jn (RNC)', 3, '10 min'),
        S('Hatia (HTE)', 8, '20–25 min'),
    ],

    // ── East / Northeast ──
    kolkata: [
        A('Netaji Subhas Chandra Bose International Airport (CCU)', 17, '45–60 min'),
        S('Howrah (HWH)', 5, '20–30 min'),
        S('Sealdah (SDAH)', 3, '10–15 min'),
        S('Kolkata (KOAA)', 6, '20–25 min'),
        S('Shalimar (SHM)', 8, '25–35 min'),
    ],
    bhubaneswar: [
        A('Biju Patnaik International Airport (BBI)', 4, '15 min'),
        S('Bhubaneswar (BBS)', 2, '10 min'),
        S('New Bhubaneswar (BBSN)', 10, '25 min'),
    ],
    puri: [
        A('Biju Patnaik International Airport (BBI)', 60, '1.5–2 hrs; traffic varies'),
        S('Puri (PURI)', 2, '10 min'),
    ],
    siliguri: [
        A('Bagdogra Airport (IXB)', 14, '30 min'),
        S('New Jalpaiguri Jn (NJP)', 6, '15–20 min'),
        S('Siliguri Jn (SGUJ)', 2, '10 min'),
    ],
    guwahati: [
        A('Lokpriya Gopinath Bordoloi International Airport (GAU)', 22, '45–60 min'),
        S('Guwahati (GHY)', 1, '5–10 min'),
        S('Kamakhya Jn (KYQ)', 8, '20–25 min'),
    ],

    // ── West / Central ──
    mumbai: [
        A('Chhatrapati Shivaji Maharaj International Airport (BOM)', 20, '45–75 min'),
        A('Navi Mumbai International Airport (NMI)', 38, '1–1.5 hrs'),
        S('Mumbai Central (MMCT)', 1, '5 min'),
        S('Chhatrapati Shivaji Maharaj Terminus (CSMT)', 4, '15 min'),
        S('Dadar (DR)', 6, '20 min'),
        S('Bandra Terminus (BDTS)', 13, '35–45 min'),
        S('Lokmanya Tilak Terminus (LTT)', 15, '40–50 min'),
    ],
    pune: [
        A('Pune Airport (PNQ)', 9, '25–35 min'),
        S('Pune Jn (PUNE)', 1, '5 min'),
    ],
    goa: [
        // distances from Panaji
        A('Manohar International Airport, Mopa (GOX)', 38, '~1 hr'),
        A('Dabolim Airport (GOI)', 28, '40–50 min'),
        S('Madgaon Jn (MAO)', 33, '50–60 min'),
        S('Karmali (KRMI)', 12, '20–25 min'),
        S('Thivim (THVM)', 20, '30–35 min'),
    ],
    ahmedabad: [
        A('Sardar Vallabhbhai Patel International Airport (AMD)', 9, '25–30 min'),
        S('Ahmedabad Jn (ADI)', 2, '10 min'),
        S('Sabarmati (SBI)', 8, '20–25 min'),
    ],
    nashik: [
        A('Nashik Ozar Airport (ISK)', 22, '35–45 min'),
        A('Mumbai Airport (BOM)', 170, '4–5 hrs'),
        S('Nashik Road (NK)', 9, '20–25 min'),
    ],
    nagpur: [
        A('Dr. Babasaheb Ambedkar International Airport (NAG)', 8, '20–25 min'),
        S('Nagpur Jn (NGP)', 1, '5 min'),
        S('Ajni (AJNI)', 4, '10–15 min'),
    ],

    // ── South ──
    bangalore: [
        A('Kempegowda International Airport (BLR)', 35, '1–1.5 hrs'),
        S('KSR Bengaluru (SBC)', 2, '10 min'),
        S('Bangalore Cantt (BNC)', 4, '15 min'),
        S('Yesvantpur Jn (YPR)', 8, '20–30 min'),
        S('SMVT Bengaluru (SMVB)', 12, '35–45 min'),
        S('Krishnarajapuram (KJM)', 15, '40–50 min'),
    ],
    mysore: [
        A('Mysuru Airport (MYQ)', 10, '20–25 min'),
        A('Kempegowda International Airport, Bengaluru (BLR)', 180, '3.5–4 hrs'),
        S('Mysuru Jn (MYS)', 2, '10 min'),
    ],
    chennai: [
        A('Chennai International Airport (MAA)', 16, '40–50 min'),
        S('Chennai Central (MAS)', 2, '10 min'),
        S('Chennai Egmore (MS)', 3, '10–15 min'),
        S('Tambaram (TBM)', 25, '50–60 min'),
    ],
    hyderabad: [
        A('Rajiv Gandhi International Airport (HYD)', 30, '45–60 min'),
        S('Hyderabad Deccan (HYB)', 1, '5 min'),
        S('Kacheguda (KCG)', 4, '15 min'),
        S('Secunderabad Jn (SC)', 8, '25–30 min'),
        S('Charlapalli (CHZ)', 18, '40–50 min'),
    ],
    pondicherry: [
        A('Puducherry Airport (PNY)', 6, '15 min'),
        A('Chennai International Airport (MAA)', 150, '3–3.5 hrs'),
        S('Puducherry (PDY)', 1, '5 min'),
        S('Villupuram Jn (VM)', 38, '~1 hr'),
    ],
    tirupati: [
        A('Tirupati Airport (TIR)', 15, '30 min'),
        S('Tirupati (TPTY)', 1, '5 min'),
        S('Renigunta Jn (RU)', 10, '20–25 min'),
    ],
    madurai: [
        A('Madurai Airport (IXM)', 12, '25–35 min'),
        S('Madurai Jn (MDU)', 1, '5 min'),
    ],
    coimbatore: [
        A('Coimbatore International Airport (CJB)', 11, '25–35 min'),
        S('Coimbatore Jn (CBE)', 1, '5 min'),
        S('Coimbatore North (CBF)', 4, '10–15 min'),
        S('Podanur Jn (PTJ)', 7, '20 min'),
    ],
    kochi: [
        A('Cochin International Airport (COK)', 28, '45–60 min'),
        S('Ernakulam Jn (ERS)', 2, '10 min'),
        S('Ernakulam Town (ERN)', 3, '10–15 min'),
        S('Aluva (AWY)', 20, '40–50 min'),
    ],
    trivandrum: [
        A('Trivandrum International Airport (TRV)', 6, '15–20 min'),
        S('Thiruvananthapuram Central (TVC)', 1, '5 min'),
        S('Thiruvananthapuram North (TVCN, ex-Kochuveli)', 8, '20–25 min'),
    ],
    visakhapatnam: [
        A('Visakhapatnam Airport (VTZ)', 12, '25–35 min'),
        S('Visakhapatnam Jn (VSKP)', 3, '10 min'),
        S('Duvvada (DVD)', 20, '40–50 min'),
    ],
    vijayawada: [
        A('Vijayawada Airport, Gannavaram (VGA)', 20, '35–45 min'),
        S('Vijayawada Jn (BZA)', 2, '10 min'),
    ],
    warangal: [
        A('Rajiv Gandhi International Airport, Hyderabad (HYD)', 160, '3–3.5 hrs'),
        S('Warangal (WL)', 2, '10 min'),
        S('Kazipet Jn (KZJ)', 10, '20–25 min'),
    ],
};

// Alternate slugs that share one gateway list
const GATEWAY_SLUG_ALIASES = {
    vizag: 'visakhapatnam',
    allahabad: 'prayagraj',
    bengaluru: 'bangalore',
    mysuru: 'mysore',
    puducherry: 'pondicherry',
    thiruvananthapuram: 'trivandrum',
};

const gatewaysFor = (toSlug) => DESTINATION_GATEWAYS[GATEWAY_SLUG_ALIASES[toSlug] || toSlug] || [];

/*
 * Scalable SEO corridor layer.
 * These pages intentionally avoid hard-coded train numbers, fares, or schedules.
 * SearchForm receives raw city coordinates (stype/dtype=google), then fetches
 * current inventory for the selected travel date.
 */
const MUMBAI = ['mumbai', 'Mumbai', 18.9582347, 72.8319514];
const DELHI = ['delhi', 'Delhi', 28.6139, 77.2090];
const BANGALORE = ['bangalore', 'Bangalore', 12.9716, 77.5946];
const HYDERABAD = ['hyderabad', 'Hyderabad', 17.3850, 78.4867];
const CHENNAI = ['chennai', 'Chennai', 13.0827, 80.2707];
const PUNE = ['pune', 'Pune', 18.5204, 73.8567];
const KOLKATA = ['kolkata', 'Kolkata', 22.5726, 88.3639];
const AHMEDABAD = ['ahmedabad', 'Ahmedabad', 23.0225, 72.5714];
const JAIPUR = ['jaipur', 'Jaipur', 26.9124, 75.7873];
const GOA = ['goa', 'Goa', 15.4909, 73.8278]; // Panaji

const CURATED_SEEDS = [
    [...MUMBAI, ...GOA],
    [...MUMBAI, ...JAIPUR],
    [...MUMBAI, 'udaipur', 'Udaipur', 24.5854, 73.7125],
    [...MUMBAI, 'varanasi', 'Varanasi', 25.3176, 82.9739],
    [...MUMBAI, 'ayodhya', 'Ayodhya', 26.7997, 82.2044],
    [...MUMBAI, 'amritsar', 'Amritsar', 31.6340, 74.8723],
    [...MUMBAI, 'dehradun', 'Dehradun', 30.3165, 78.0322],
    [...MUMBAI, 'rishikesh', 'Rishikesh', 30.0869, 78.2676],
    [...DELHI, ...GOA],
    [...DELHI, 'dehradun', 'Dehradun', 30.3165, 78.0322],
    [...DELHI, 'rishikesh', 'Rishikesh', 30.0869, 78.2676],
    [...DELHI, 'manali', 'Manali', 32.2396, 77.1887],
    [...DELHI, 'amritsar', 'Amritsar', 31.6340, 74.8723],
    [...DELHI, ...JAIPUR],
    [...BANGALORE, ...GOA],
    [...BANGALORE, 'coorg', 'Coorg', 12.4244, 75.7382],
    [...BANGALORE, 'mysore', 'Mysore', 12.2958, 76.6394],
    [...BANGALORE, ...HYDERABAD],
    [...BANGALORE, ...CHENNAI],
    [...BANGALORE, 'pondicherry', 'Pondicherry', 11.9416, 79.8083],
    [...HYDERABAD, ...GOA],
    [...HYDERABAD, 'tirupati', 'Tirupati', 13.6288, 79.4192],
    [...HYDERABAD, 'vizag', 'Visakhapatnam', 17.6868, 83.2185],
    [...CHENNAI, 'pondicherry', 'Pondicherry', 11.9416, 79.8083],
    [...CHENNAI, 'madurai', 'Madurai', 9.9252, 78.1198],
    [...CHENNAI, 'coimbatore', 'Coimbatore', 11.0168, 76.9558],
    [...PUNE, ...GOA],
    [...PUNE, 'nashik', 'Nashik', 19.9975, 73.7898],
    [...KOLKATA, 'siliguri', 'Siliguri', 26.7271, 88.3953],
    [...KOLKATA, 'puri', 'Puri', 19.8135, 85.8312],

    [...MUMBAI, ...DELHI],
    [...MUMBAI, 'patna', 'Patna', 25.5941, 85.1376],
    [...MUMBAI, 'darbhanga', 'Darbhanga', 26.1542, 85.8918],
    [...MUMBAI, 'gorakhpur', 'Gorakhpur', 26.7606, 83.3732],
    [...MUMBAI, 'prayagraj', 'Prayagraj', 25.4358, 81.8463],
    [...MUMBAI, 'kanpur', 'Kanpur', 26.4499, 80.3319],
    [...MUMBAI, 'lucknow', 'Lucknow', 26.8467, 80.9462],
    [...MUMBAI, 'ranchi', 'Ranchi', 23.3441, 85.3096],
    [...MUMBAI, 'bhubaneswar', 'Bhubaneswar', 20.2961, 85.8245],
    [...MUMBAI, 'nagpur', 'Nagpur', 21.1458, 79.0882],
    [...DELHI, 'patna', 'Patna', 25.5941, 85.1376],
    [...DELHI, 'darbhanga', 'Darbhanga', 26.1542, 85.8918],
    [...DELHI, 'gorakhpur', 'Gorakhpur', 26.7606, 83.3732],
    [...DELHI, 'varanasi', 'Varanasi', 25.3176, 82.9739],
    [...DELHI, 'prayagraj', 'Prayagraj', 25.4358, 81.8463],
    [...DELHI, 'lucknow', 'Lucknow', 26.8467, 80.9462],
    [...DELHI, 'ranchi', 'Ranchi', 23.3441, 85.3096],
    [...DELHI, 'bhubaneswar', 'Bhubaneswar', 20.2961, 85.8245],
    [...DELHI, ...KOLKATA],
    [...DELHI, ...AHMEDABAD],
    [...BANGALORE, ...MUMBAI],
    [...BANGALORE, ...PUNE],
    [...BANGALORE, 'kochi', 'Kochi', 9.9312, 76.2673],
    [...BANGALORE, 'trivandrum', 'Thiruvananthapuram', 8.5241, 76.9366],
    [...BANGALORE, 'madurai', 'Madurai', 9.9252, 78.1198],
    [...BANGALORE, 'vijayawada', 'Vijayawada', 16.5062, 80.6480],
    [...BANGALORE, 'visakhapatnam', 'Visakhapatnam', 17.6868, 83.2185],
    [...HYDERABAD, ...BANGALORE],
    [...HYDERABAD, ...CHENNAI],
    [...HYDERABAD, ...MUMBAI],
    [...HYDERABAD, ...PUNE],
    [...HYDERABAD, 'vijayawada', 'Vijayawada', 16.5062, 80.6480],
    [...HYDERABAD, 'warangal', 'Warangal', 17.9689, 79.5941],
    [...CHENNAI, ...BANGALORE],
    [...CHENNAI, ...HYDERABAD],
    [...CHENNAI, 'kochi', 'Kochi', 9.9312, 76.2673],
    [...CHENNAI, 'trivandrum', 'Thiruvananthapuram', 8.5241, 76.9366],
    [...PUNE, ...MUMBAI],
    [...PUNE, ...DELHI],
    [...PUNE, ...HYDERABAD],
    [...PUNE, ...BANGALORE],
    [...KOLKATA, ...DELHI],
    [...KOLKATA, 'patna', 'Patna', 25.5941, 85.1376],
    [...KOLKATA, 'varanasi', 'Varanasi', 25.3176, 82.9739],
    [...KOLKATA, 'ranchi', 'Ranchi', 23.3441, 85.3096],
    [...AHMEDABAD, ...MUMBAI],
    [...AHMEDABAD, ...DELHI],
    [...AHMEDABAD, ...JAIPUR],
    [...JAIPUR, ...DELHI],
    [...JAIPUR, ...MUMBAI],
];

/* Festival (Diwali/Chhath) matrix: origin × destination, generated.
   `limit` caps how many destinations (in list order) an origin gets. */
const FESTIVAL_DESTINATIONS = [
    ['ayodhya', 'Ayodhya', 26.7997, 82.2044],
    ['bhubaneswar', 'Bhubaneswar', 20.2961, 85.8245],
    ['darbhanga', 'Darbhanga', 26.1542, 85.8918],
    ['gaya', 'Gaya', 24.7914, 85.0002],
    ['gorakhpur', 'Gorakhpur', 26.7606, 83.3732],
    ['guwahati', 'Guwahati', 26.1445, 91.7362],
    ['kanpur', 'Kanpur', 26.4499, 80.3319],
    ['kolkata', 'Kolkata', 22.5726, 88.3639],
    ['lucknow', 'Lucknow', 26.8467, 80.9462],
    ['muzaffarpur', 'Muzaffarpur', 26.1209, 85.3647],
    ['patna', 'Patna', 25.5941, 85.1376],
    ['prayagraj', 'Prayagraj', 25.4358, 81.8463],
    ['ranchi', 'Ranchi', 23.3441, 85.3096],
    ['siliguri', 'Siliguri', 26.7271, 88.3953],
    ['varanasi', 'Varanasi', 25.3176, 82.9739],
];

const FESTIVAL_ORIGINS = [
    { seed: ['agra', 'Agra', 27.1767, 78.0081] },
    { seed: AHMEDABAD },
    { seed: ['allahabad', 'Prayagraj', 25.4358, 81.8463] },
    { seed: ['amritsar', 'Amritsar', 31.634, 74.8723] },
    { seed: ['aurangabad', 'Aurangabad', 19.8762, 75.3433] },
    { seed: ['ayodhya', 'Ayodhya', 26.7997, 82.2044] },
    { seed: BANGALORE },
    { seed: ['bareilly', 'Bareilly', 28.367, 79.4304] },
    { seed: ['belgaum', 'Belagavi', 15.8497, 74.4977] },
    { seed: ['bhopal', 'Bhopal', 23.2599, 77.4126] },
    { seed: ['bhubaneswar', 'Bhubaneswar', 20.2961, 85.8245] },
    { seed: ['bhuj', 'Bhuj', 23.242, 69.6669] },
    { seed: ['bilaspur', 'Bilaspur', 22.0797, 82.1409] },
    { seed: ['chandigarh', 'Chandigarh', 30.7333, 76.7794], limit: 7 },
];

const FESTIVAL_SEEDS = FESTIVAL_ORIGINS.flatMap(({ seed, limit }) =>
    FESTIVAL_DESTINATIONS.slice(0, limit ?? FESTIVAL_DESTINATIONS.length).map((dest) => [...seed, ...dest])
);

const SEO_CORRIDOR_SEEDS = [...CURATED_SEEDS, ...FESTIVAL_SEEDS];

const SEO_ROUTE_GUIDANCE = {
  'mumbai-to-goa': { train: 'Compare direct trains to Madgaon (MAO) or Karmali (KRMI), depending on the part of Goa you are visiting.', bus: 'Overnight buses from Mumbai to Goa are commonly available, with arrival points around Panaji, Mapusa or Margao depending on the service.' },
  'mumbai-to-jaipur': { train: 'Check direct trains to Jaipur Junction (JP), including services from Mumbai Central and Bandra Terminus where available.', bus: 'Direct and overnight buses connect Mumbai with Jaipur; verify the arrival boarding area and final city transfer.' },
  'mumbai-to-udaipur': { train: 'Look for trains to Udaipur City (UDZ) or Rana Pratap Nagar (RPZ), including direct or one-change journeys.', bus: 'Overnight buses connect Mumbai with Udaipur, generally arriving at the city bus terminal or nearby highway stops.' },
  'mumbai-to-varanasi': { train: 'Compare direct trains to Varanasi Junction (BSB), Banaras (BSBS) and nearby major stations when available.', bus: 'Long-distance buses may operate directly or through northern hubs; check the complete arrival point and duration.' },
  'mumbai-to-ayodhya': { train: 'Compare trains to Ayodhya Dham Junction (AY) and Ayodhya Cantt (AYC), along with break journeys through major junctions such as Gonda.', bus: 'Direct buses may be available, while flight-plus-bus options can connect through Lucknow or Varanasi with a separate onward bus leg.' },
  'mumbai-to-amritsar': { train: 'Check direct trains to Amritsar Junction (ASR) and alternatives through New Delhi when direct availability is limited.', bus: 'Overnight buses may connect Mumbai with Amritsar directly or through Delhi; check the final arrival point.' },
  'mumbai-to-dehradun': { train: 'Compare trains to Dehradun (DDN), including direct services and connections through Delhi or Haridwar.', bus: 'Overnight buses generally connect Mumbai with Dehradun directly or through Delhi, depending on the date.' },
  'mumbai-to-rishikesh': { train: 'Use Haridwar Junction (HW) or Rishikesh station (YNRK) as rail gateways, then complete the final local transfer where required.', bus: 'Buses may reach Rishikesh directly or arrive at Haridwar/Dehradun with a short onward road journey.' },
  'delhi-to-goa': { train: 'Compare trains to Madgaon (MAO), Vasco da Gama (VSG) or Karmali (KRMI), depending on your Goa destination.', bus: 'Long-distance buses may be direct or require a connection; check the exact Goa arrival city before booking.' },
  'delhi-to-dehradun': { train: 'Direct trains to Dehradun (DDN) are available on selected dates; compare them with services through Haridwar.', bus: 'Direct buses from Delhi to Dehradun are commonly available, with multiple boarding and dropping points.' },
  'delhi-to-rishikesh': { train: 'Compare trains to Haridwar Junction (HW) and Rishikesh (YNRK), followed by a short local transfer if needed.', bus: 'Direct buses from Delhi to Rishikesh are commonly available, including overnight services.' },
  'delhi-to-manali': { train: 'There is no railway station in Manali; compare trains to Chandigarh or Ambala Cantt followed by a bus or cab to Manali.', bus: 'Overnight Volvo and ordinary buses commonly connect Delhi with Manali, subject to seasonal schedules.' },
  'delhi-to-amritsar': { train: 'Compare direct trains to Amritsar Junction (ASR), including high-speed and overnight options where available.', bus: 'Direct buses from Delhi to Amritsar are commonly available throughout the week.' },
  'delhi-to-jaipur': { train: 'Compare direct trains to Jaipur Junction (JP), including fast daytime services and conventional trains.', bus: 'Frequent direct buses connect Delhi with Jaipur, with several boarding points around Delhi.' },
  'bangalore-to-goa': { train: 'Compare trains to Madgaon (MAO), Karmali (KRMI) and Vasco da Gama (VSG), depending on your destination in Goa.', bus: 'Overnight buses from Bengaluru to Goa commonly arrive around Panaji, Mapusa or Margao.' },
  'bangalore-to-coorg': { train: 'Recommended rail route: take a train from Bengaluru to Mysuru Junction (MYS), then continue by cab to Madikeri/Coorg. Coorg has no railway station; direct buses to Madikeri are another option.', bus: 'Direct buses to Madikeri are commonly available from Bengaluru, with some services continuing towards Kushalnagar.' },
  'bangalore-to-mysore': { train: 'Compare direct trains to Mysuru Junction (MYS), including frequent daytime services.', bus: 'Frequent direct buses connect Bengaluru with Mysuru from multiple city boarding points.' },
  'bangalore-to-hyderabad': { train: 'Compare trains to Secunderabad Junction (SC), Hyderabad Deccan (HYB) and Kacheguda (KCG).', bus: 'Direct overnight buses commonly connect Bengaluru with Hyderabad, with multiple city boarding and dropping points.' },
  'bangalore-to-chennai': { train: 'Compare direct trains to Chennai Central (MAS) and Chennai Egmore (MS), depending on the service.', bus: 'Frequent direct buses connect Bengaluru with Chennai, including daytime and overnight services.' },
  'bangalore-to-pondicherry': { train: 'Compare trains to Villupuram Junction (VM), followed by an approximately 38 km road transfer to Puducherry, or services through Chennai.', bus: 'Direct buses from Bengaluru to Puducherry are commonly available, including overnight and daytime services.' },
  'hyderabad-to-goa': { train: 'Compare trains to Madgaon (MAO), Vasco da Gama (VSG) or nearby stations based on your final Goa destination.', bus: 'Overnight buses may connect Hyderabad with Goa directly, with arrival points varying by city.' },
  'hyderabad-to-tirupati': { train: 'Compare direct trains to Tirupati (TPTY) and Renigunta Junction (RU), followed by a short local transfer when required.', bus: 'Direct buses from Hyderabad to Tirupati are commonly available, including overnight services.' },
  'hyderabad-to-vizag': { train: 'Compare trains to Visakhapatnam Junction (VSKP) and nearby Duvvada (DVD) when relevant.', bus: 'Direct overnight buses commonly connect Hyderabad with Visakhapatnam, with arrival points varying by operator.' },
  'chennai-to-pondicherry': { train: 'Compare trains to Villupuram Junction (VM), followed by an approximately 38 km road transfer, or rail options through nearby stations.', bus: 'Frequent direct buses connect Chennai with Puducherry from several boarding points.' },
  'chennai-to-madurai': { train: 'Compare direct trains to Madurai Junction (MDU), including daytime and overnight services.', bus: 'Direct buses from Chennai to Madurai are commonly available, including overnight services.' },
  'chennai-to-coimbatore': { train: 'Compare direct trains to Coimbatore Junction (CBE) and Coimbatore North (CBF), depending on the service.', bus: 'Frequent direct buses connect Chennai with Coimbatore, including overnight options.' },
  'pune-to-goa': { train: 'Compare trains to Madgaon (MAO), Karmali (KRMI) or Vasco da Gama (VSG), depending on your destination in Goa.', bus: 'Overnight buses commonly connect Pune with Goa, with arrival points around Panaji, Mapusa or Margao.' },
  'pune-to-nashik': { train: 'Compare trains to Nashik Road (NK), followed by a local transfer into Nashik city.', bus: 'Direct buses commonly connect Pune with Nashik throughout the day.' },
  'kolkata-to-siliguri': { train: 'Compare trains to New Jalpaiguri Junction (NJP), the principal rail gateway for Siliguri.', bus: 'Direct buses from Kolkata to Siliguri are commonly available, including overnight services.' },
  'kolkata-to-puri': { train: 'Compare direct trains to Puri Railway Station (PURI); Puri has its own station, so an onward road transfer is not necessary for rail arrivals there.', bus: 'Direct buses connect Kolkata with Puri, while some services terminate in Bhubaneswar and require onward road travel.' }
};

const SEO_DIWALI_CORRIDORS = new Set([
    'mumbai-to-patna','mumbai-to-darbhanga','mumbai-to-gorakhpur','mumbai-to-prayagraj','mumbai-to-kanpur','mumbai-to-lucknow','mumbai-to-ranchi','mumbai-to-bhubaneswar',
    'delhi-to-patna','delhi-to-darbhanga','delhi-to-gorakhpur','delhi-to-varanasi','delhi-to-prayagraj','delhi-to-ranchi','delhi-to-bhubaneswar','delhi-to-kolkata',
    'kolkata-to-patna','kolkata-to-varanasi','kolkata-to-ranchi','ahmedabad-to-delhi','jaipur-to-delhi',
    ...['ahmedabad', 'bangalore'].flatMap((origin) =>
        FESTIVAL_DESTINATIONS.map(([destSlug]) => `${origin}-to-${destSlug}`)
    ),
]);

const makeSeoCorridor = ([fromSlug, fromName, fromLat, fromLng, toSlug, toName, toLat, toLng]) => {
    const corridorKey = `${fromSlug}-to-${toSlug}`;
    const guidance = SEO_ROUTE_GUIDANCE[corridorKey] || {};
    const diwaliFocused = SEO_DIWALI_CORRIDORS.has(corridorKey);
    return ({
    from: { name: fromName, lat: fromLat, lng: fromLng, type: 'google' },
    to: { name: toName, lat: toLat, lng: toLng, type: 'google' },
    title: `${fromName} to ${toName}: Train, Bus, Flight & Mixed-Mode Travel Guide`,
    metaDescription: `Plan ${fromName} to ${toName} by train, bus, flight or mixed mode. Compare journey time, transfers and date-specific route availability with RouteChef${diwaliFocused ? ' for Diwali and Chhath travel planning' : ''}.`,
    quickAnswer: QUICK_ANSWERS[corridorKey] || `From ${fromName} to ${toName}, compare flight, bus and train options for your date. Check the complete journey, including transfers and last-mile travel, before booking.`,
    intro: [
        `${fromName} to ${toName} can be planned through train, bus, flight and mixed-mode options. Flight, bus and train availability changes by date, so compare total journey time, price, transfer requirements and last-mile travel.`,
        `${diwaliFocused ? 'During Diwali and Chhath, popular rail corridors can experience heavy demand, so check early and compare bus or flight-plus-road alternatives when trains are waitlisted. ' : ''}RouteChef lets you start with the city pair, choose a travel date and compare available routes. When a direct option is unavailable or inconvenient, check connecting combinations and nearby gateways before committing to a booking.`,
    ],
    gateways: gatewaysFor(toSlug),
    options: [
        {
            id: 'flight', icon: 'flight', badge: 'Compare flight options',
            title: `${fromName} to ${toName} flight`,
            duration: 'Depends on schedule and onward transfer',
            fareNote: 'Live fare shown after search', modesParam: 'Flight',
            body: `Search flights between the nearest practical airports for ${fromName} and ${toName}. Include airport transfers and the final road leg when comparing the total door-to-door journey.`,
        },
        {
            id: 'bus', icon: 'bus', badge: 'Compare bus options',
            title: `${fromName} to ${toName} bus`,
            duration: 'Depends on operator and route',
            fareNote: 'Live fare shown after search', modesParam: 'Bus',
            body: guidance.bus || `Check direct and connecting buses from ${fromName} to ${toName}. Boarding points, arrival stops and availability vary by date, so review the complete journey before booking.`,
        },
        {
            id: 'train', icon: 'train', badge: 'Compare train options',
            title: `${fromName} to ${toName} train`,
            duration: 'Depends on direct and connecting services',
            fareNote: 'Live availability shown after search', modesParam: 'Train',
            body: guidance.train || `Compare direct trains and practical break journeys between ${fromName} and ${toName}. Include the final cab or bus leg when the destination is away from the arrival station.`,
        },
        {
            id: 'mixed', icon: 'flight', badge: 'Mixed-mode planning',
            title: `${fromName} to ${toName} mixed-mode options`,
            duration: 'Depends on connection and transfer buffer',
            fareNote: 'Compare current available segments', modesParam: 'All',
            body: `A mixed route may help when a direct option is unavailable or poorly timed. Review transfer time, separate-ticket responsibilities and cancellation conditions before booking each segment.`,
        },
    ],
    steps: [
        `Select ${fromName} as the origin and ${toName} as the destination.`,
        'Choose your travel date and search available routes.',
        'Compare total price, duration, connection time and boarding points.',
        'Recheck live availability and each supplier\'s cancellation terms before booking.',
    ],
    faqs: [
        { q: `What is the best way to reach ${toName} from ${fromName}?`, a: `It depends on the date, budget and live availability. Compare flight, bus, train and mixed routes using RouteChef rather than relying only on the fastest or cheapest segment.` },
        { q: `Can I search buses from ${fromName} to ${toName}?`, a: `Use the live search to check bus inventory for your selected date. Boarding points, operators and availability can change by date.` },
        { q: `Can I combine flight and bus travel?`, a: 'Where supported by available inventory, RouteChef can help you compare mixed-mode journeys. Verify each segment, transfer time and separate booking conditions before payment.' },
        { q: `Are fares and travel times fixed?`, a: 'No. Fares, availability, delays and transfer times change. Treat indicative guide information as planning context and rely on live search results before booking.' },
        ...(diwaliFocused ? [{ q: `How can I handle Diwali train waitlists from ${fromName} to ${toName}?`, a: `Check train availability early, compare alternate boarding or arrival stations where practical, and review bus, flight and mixed-mode alternatives. Festival specials and inventory can change, so confirm the live result for your date before booking.` }] : []),
    ],
    });
};

const SEO_CORRIDORS = {};
for (const seed of SEO_CORRIDOR_SEEDS) {
    const [fromSlug, fromName, , , toSlug, toName] = seed;
    const key = `${fromSlug}-to-${toSlug}`;
    if (fromName === toName) continue;              // no self-loops (e.g. allahabad → prayagraj)
    if (BASE_CORRIDORS[key] || SEO_CORRIDORS[key]) continue; // hand-written pages win; first seed wins
    SEO_CORRIDORS[key] = makeSeoCorridor(seed);
}

export const CORRIDORS = { ...BASE_CORRIDORS, ...SEO_CORRIDORS };
